import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCatalog } from '../context/CatalogContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AddressAutocomplete from '../components/AddressAutocomplete';
import AddressRegionFields from '../components/AddressRegionFields';
import { saveErrorText } from '../constants/addressRegions';
import Button from '../components/Button';
import DocumentsPanel from '../components/DocumentsPanel';
import CardGifPanel from '../components/CardGifPanel';
import {
  EditorHero,
  MonoLabel,
  Monogram,
  SectionCap,
  Field,
  EditorialButton,
  Pill,
  EmptyState,
} from '../components/Editorial';

const PROFILE_TABS = [
  { label: 'Info', to: '/profile', active: true },
  { label: 'Academics', to: '/academics' },
  { label: 'Photos', to: '/photos' },
  { label: 'Videos', to: '/videos' },
  { label: 'Social', to: '/social' },
  { label: 'My Sports', to: '/my-sports' },
  { label: 'Stats', to: '/stats' },
  { label: 'Measurables', to: '/measurables' },
  { label: 'Preferences', to: '/preferences' },
];

// Resolves a sport's saved coach-contact row purely from its inputs — no
// closure over component state — so it's safe to call from render or from an
// async handler without any dependency-array entanglement. Matches by
// catalog NAME -> slug -> row.sport_slug, never by falling back to a default
// slug: an unresolvable sportName returns null rather than guessing, so it
// can never return another sport's row.
function matchedSportCoachRow(catalogSports, sportCoaches, sportName) {
  const sportEntry = (catalogSports || []).find(s => s.name === sportName);
  if (!sportEntry) return null;
  return (sportCoaches || []).find(r => r.sport_slug === sportEntry.slug) || null;
}

export default function AthleteProfilePage() {
  const { user, API_URL } = useAuth();
  const catalog = useCatalog();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [previewModal, setPreviewModal] = useState(null);

  // Per-sport HS + club coach contact — `coachDrafts` holds ONLY fields
  // actually typed into during this mount, keyed by the sport's DISPLAY NAME
  // (never a resolved slug — see matchedSportCoachRow above). It starts
  // empty and is never seeded from the server: seeding a snapshot at fetch
  // time raced CatalogContext's async `/sports/catalog` fetch — if the
  // catalog lost that race, a real saved coach row for a non-football sport
  // got silently mapped to nothing and the block rendered blank even though
  // the server had the data. Instead, the displayed value and the diff
  // baseline are both *derived at render* from `profile.sport_coaches`
  // (already present on `profile` — it's part of the profile payload) via
  // matchedSportCoachRow, resolved against whatever `catalog.sports` looks
  // like right now. The moment the catalog finishes loading, the very next
  // render matches correctly — no extra effect, no stale snapshot.
  const [coachDrafts, setCoachDrafts] = useState({});

  const [profile, setProfile] = useState({
    gender: '',
    cell_phone: '',
    home_address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    guardian1_name: '',
    guardian1_phone: '',
    guardian1_email: '',
    guardian2_name: '',
    guardian2_phone: '',
    guardian2_email: '',
    personal_statement: '',
    personal_video_url: '',
    height_inches: null,
    weight_lbs: null,
    dash_40: null,
    vertical_jump: null,
    gpa_weighted: null,
    gpa_unweighted: null,
    high_school: '',
    graduation_year: '',
    sports_played: [],
    position_primary: '',
    position_secondary: '',
    player_photo: '',
  });

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setPreviewModal(null); };
    if (previewModal) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [previewModal]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/athletes/profile`, getAxiosConfig());
      if (response.data) {
        // `response.data.sport_coaches` lands on `profile.sport_coaches` via
        // this spread — no separate coach state to seed. See coachDrafts'
        // comment above for why the coach blocks read from `profile`
        // directly (matched against the catalog at render time) instead of
        // a snapshot taken here.
        setProfile(prev => ({ ...prev, ...response.data }));
        if (response.data.profile_url) setProfileUrl(response.data.profile_url);
      }
    } catch (error) {
      console.log('No existing profile or error:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile({
      ...profile,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const saveField = async (name, value) => {
    try {
      await axios.post(
        `${API_URL}/athletes/profile`,
        { [name]: value },
        getAxiosConfig()
      );
      setMessage('Saved');
      setMessageType('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Auto-save error:', error);
      setMessage(saveErrorText(error));
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const saveFields = async (patch) => {
    try {
      await axios.post(`${API_URL}/athletes/profile`, patch, getAxiosConfig());
      setMessage('Saved');
      setMessageType('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Auto-save error:', error);
      setMessage(saveErrorText(error));
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
    }
  };

  // A pick from the address autocomplete: country is saved only when Places
  // returned one, so a legacy NULL country is never overwritten by ''.
  const applyAddressSelection = (addressData) => {
    const { country, ...rest } = addressData;
    const patch = country ? addressData : rest;
    setProfile(prev => ({ ...prev, ...patch }));
    saveFields(patch);
  };

  // AddressRegionFields: the full triple on every edit. Only a country switch
  // writes country (and the cleared state/postal with it).
  const handleRegionChange = (v, meta) => {
    if (meta && meta.countryChanged) {
      setProfile(prev => ({ ...prev, ...v }));
      saveFields({ country: v.country, state: v.state, zip_code: v.zip_code });
    } else {
      setProfile(prev => ({ ...prev, state: v.state, zip_code: v.zip_code }));
    }
  };

  const handleBlur = async (e) => {
    const { name } = e.target;
    await saveField(name, profile[name]);
  };

  /* ── Per-sport coach contacts ──
     "The athlete's sports" = primary sport + other sports played, both
     stored as display names (multi-sport standing rule: no hardcoded sport
     list). Deduped, order-preserving. */
  const athleteSports = (() => {
    const seen = new Set();
    const out = [];
    [profile.sport, ...(Array.isArray(profile.sports_played) ? profile.sports_played : [])].forEach(name => {
      const trimmed = (name || '').trim();
      if (trimmed && !seen.has(trimmed)) { seen.add(trimmed); out.push(trimmed); }
    });
    return out;
  })();

  // Whether the catalog actually recognizes this display-name string. A
  // sport that doesn't resolve gets its block disabled (see the render
  // section below) rather than silently matched against another sport's row.
  const isCatalogSport = (sportName) => catalog.sports.some(s => s.name === sportName);

  const coachValue = (sportName, field) => {
    const draft = coachDrafts[sportName];
    if (draft && draft[field] !== undefined) return draft[field];
    const row = matchedSportCoachRow(catalog.sports, profile.sport_coaches, sportName);
    return (row && row[field]) || '';
  };

  const handleCoachChange = (sportName, field) => (e) => {
    const value = e.target.value;
    setCoachDrafts(prev => ({
      ...prev,
      [sportName]: { ...(prev[sportName] || {}), [field]: value },
    }));
  };

  const handleCoachBlur = (sportName, field) => async (e) => {
    const value = e.target.value;
    const matched = matchedSportCoachRow(catalog.sports, profile.sport_coaches, sportName);
    const baselineVal = (matched && matched[field]) || '';
    if (value === baselineVal) return; // unchanged — send no request at all
    try {
      const res = await axios.put(
        `${API_URL}/athletes/profile/sport-coaches`,
        { sport: sportName, [field]: value },
        getAxiosConfig()
      );
      // Merge the server's authoritative row (it resolved `sport` to a real
      // sport_slug) into `profile.sport_coaches` — the single source of
      // truth the coach blocks read from — rather than a separate snapshot.
      setProfile(prev => ({
        ...prev,
        sport_coaches: [
          ...(prev.sport_coaches || []).filter(r => r.sport_slug !== res.data.sport_slug),
          res.data,
        ],
      }));
      setMessage('Saved');
      setMessageType('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Coach contact save error:', error);
      setMessage('Error saving');
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
    }
  };

  /* ── Helpers for hero ── */
  const formatHeight = (inches) => {
    if (!inches) return null;
    // Round to whole inches BEFORE dividing — flooring first then rounding the
    // remainder renders 71.6 as 5'12" instead of 6'0".
    const total = Math.round(inches);
    return `${Math.floor(total / 12)}'${total % 12}"`;
  };
  const initials = ((user?.first_name || '')[0] || '') + ((user?.last_name || '')[0] || '');
  const sport = (profile.sports_played || []).join(', ');
  const positions = [profile.position_primary, profile.position_secondary].filter(Boolean).join(' / ');
  const sportLine = [sport, positions].filter(Boolean).join(' · ');
  const subParts = [];
  if (profile.graduation_year) subParts.push(`Class of '${profile.graduation_year.toString().slice(-2)}`);
  const schoolName = profile.school ?? profile.high_school;
  if (schoolName) subParts.push([schoolName, [profile.city, profile.state].filter(Boolean).join(', ')].filter(Boolean).join(' — '));
  if (sportLine) subParts.push(sportLine);

  const stats = [
    { label: 'HEIGHT', value: formatHeight(profile.height_inches) },
    { label: 'WEIGHT', value: profile.weight_lbs ? `${profile.weight_lbs}` : null },
    { label: '40-YARD', value: profile.dash_40 || null },
    { label: 'VERTICAL', value: profile.vertical_jump ? `${profile.vertical_jump}"` : null },
    { label: 'GPA', value: profile.gpa_weighted || profile.gpa_unweighted || null },
  ];

  return (
    <div className="ed-page">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">{message}</div>
      )}

      <EditorHero
        eyebrow={
          <>
            <MonoLabel onDark>PROFILE · INFO</MonoLabel>
            {schoolName && <MonoLabel onDark>·</MonoLabel>}
            {schoolName && <MonoLabel onDark>{schoolName.toUpperCase()}</MonoLabel>}
          </>
        }
        prefix={<Monogram initials={initials} photoUrl={profile.player_photo || null} />}
        headline={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
        sub={subParts.join(' · ')}
        actions={profileUrl ? (
          <>
            <EditorialButton variant="solid" onClick={() => setPreviewModal('profile')}>
              Public Profile
            </EditorialButton>
            <EditorialButton variant="ghost" onClick={() => setPreviewModal('card')}>
              Player Card
            </EditorialButton>
          </>
        ) : null}
        stats={stats}
      />

      {/* Tab bar */}
      <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {PROFILE_TABS.map(tab => (
          tab.active ? (
            <Pill key={tab.label} variant="active">{tab.label}</Pill>
          ) : (
            <Link key={tab.label} to={tab.to} style={{ textDecoration: 'none' }}>
              <Pill variant="ghost">{tab.label}</Pill>
            </Link>
          )
        ))}
      </div>

      {/* Your player card: the nightly flip GIF. Renders nothing without one. */}
      <CardGifPanel />

      {/* Contact Information */}
      <SectionCap label="Contact Information" hint="HOW COACHES REACH YOU" />
      <div className="ed-card">
        <Field label="Home Address">
          <AddressAutocomplete
            id="home_address"
            name="home_address"
            value={profile.home_address}
            className="ed-field__input"
            placeholder="Start typing an address..."
            onManualChange={handleChange}
            onBlur={handleBlur}
            onSelect={applyAddressSelection}
          />
        </Field>
        <Field label="Apt / Suite / Unit">
          <input
            id="address_line_2"
            type="text"
            name="address_line_2"
            value={profile.address_line_2 || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            className="ed-field__input"
            placeholder="Apt 4B, Suite 200, etc."
          />
        </Field>
        <Field label="City">
          <input id="city" type="text" name="city" value={profile.city || ''} onChange={handleChange} onBlur={handleBlur} className="ed-field__input" />
        </Field>
        <AddressRegionFields
          country={profile.country}
          state={profile.state}
          postal={profile.zip_code}
          onChange={handleRegionChange}
          onBlur={(name) => saveField(name, profile[name])}
          Field={Field}
          inputClass="ed-field__input"
          selectClass="ed-field__select"
          rowClass="ed-field__row"
        />
        <Field label="Cell Phone">
          <input id="cell_phone" type="tel" name="cell_phone" value={profile.cell_phone || ''} onChange={handleChange} onBlur={handleBlur} className="ed-field__input" placeholder="(555) 123-4567" />
        </Field>
        <Field label="Gender">
          <select id="gender" name="gender" value={profile.gender || ''} onChange={handleChange} onBlur={handleBlur} className="ed-field__select">
            <option value="">Select...</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </Field>
      </div>

      {/* Coach Contacts — one block per sport the athlete plays. A two-sport
          athlete has two different HS coaches, and club teams are per-sport. */}
      {athleteSports.length === 0 ? (
        <>
          <SectionCap label="Coach Contacts" hint="HELPS COLLEGE COACHES FOLLOW UP" />
          <EmptyState title="No sport selected" body="Pick a sport under My Sports to add a coach contact." />
        </>
      ) : athleteSports.map(sportName => (
        <React.Fragment key={sportName}>
          <SectionCap label={`Coach Contact — ${sportName}`} hint="HELPS COLLEGE COACHES FOLLOW UP" />
          {isCatalogSport(sportName) ? (
            <div className="ed-card">
              <Field label="HS Coach Name">
                <input type="text" value={coachValue(sportName, 'hs_coach_name')}
                  onChange={handleCoachChange(sportName, 'hs_coach_name')}
                  onBlur={handleCoachBlur(sportName, 'hs_coach_name')} className="ed-field__input" />
              </Field>
              <div className="ed-field__row">
                <Field label="HS Coach Email">
                  <input type="email" value={coachValue(sportName, 'hs_coach_email')}
                    onChange={handleCoachChange(sportName, 'hs_coach_email')}
                    onBlur={handleCoachBlur(sportName, 'hs_coach_email')} className="ed-field__input" />
                </Field>
                <Field label="HS Coach Phone">
                  <input type="tel" value={coachValue(sportName, 'hs_coach_phone')}
                    onChange={handleCoachChange(sportName, 'hs_coach_phone')}
                    onBlur={handleCoachBlur(sportName, 'hs_coach_phone')} className="ed-field__input" placeholder="(555) 123-4567" />
                </Field>
              </div>
              <Field label="Club Team Name">
                <input type="text" value={coachValue(sportName, 'club_team_name')}
                  onChange={handleCoachChange(sportName, 'club_team_name')}
                  onBlur={handleCoachBlur(sportName, 'club_team_name')} className="ed-field__input" />
              </Field>
              <Field label="Club Coach Name">
                <input type="text" value={coachValue(sportName, 'club_coach_name')}
                  onChange={handleCoachChange(sportName, 'club_coach_name')}
                  onBlur={handleCoachBlur(sportName, 'club_coach_name')} className="ed-field__input" />
              </Field>
              <div className="ed-field__row">
                <Field label="Club Coach Email">
                  <input type="email" value={coachValue(sportName, 'club_coach_email')}
                    onChange={handleCoachChange(sportName, 'club_coach_email')}
                    onBlur={handleCoachBlur(sportName, 'club_coach_email')} className="ed-field__input" />
                </Field>
                <Field label="Club Coach Phone">
                  <input type="tel" value={coachValue(sportName, 'club_coach_phone')}
                    onChange={handleCoachChange(sportName, 'club_coach_phone')}
                    onBlur={handleCoachBlur(sportName, 'club_coach_phone')} className="ed-field__input" placeholder="(555) 123-4567" />
                </Field>
              </div>
            </div>
          ) : (
            <div className="ed-card">
              <span className="ed-field__hint">
                “{sportName}” isn't in our sport catalog yet, so a coach contact can't be saved for it.
              </span>
            </div>
          )}
        </React.Fragment>
      ))}

      {/* Guardian Information */}
      <SectionCap label="Guardian Information" hint="RECRUITING COMPLIANCE" />
      <div className="ed-card">
        <div className="ed-field__row">
          <Field label="Primary Guardian Name">
            <input id="guardian1_name" type="text" name="guardian1_name" value={profile.guardian1_name || ''} onChange={handleChange} onBlur={handleBlur} className="ed-field__input" />
          </Field>
          <Field label="Primary Guardian Phone">
            <input id="guardian1_phone" type="tel" name="guardian1_phone" value={profile.guardian1_phone || ''} onChange={handleChange} onBlur={handleBlur} className="ed-field__input" />
          </Field>
        </div>
        <Field label="Primary Guardian Email">
          <input id="guardian1_email" type="email" name="guardian1_email" value={profile.guardian1_email || ''} onChange={handleChange} onBlur={handleBlur} className="ed-field__input" />
        </Field>
        <div className="ed-field__row">
          <Field label="Secondary Guardian Name">
            <input id="guardian2_name" type="text" name="guardian2_name" value={profile.guardian2_name || ''} onChange={handleChange} onBlur={handleBlur} className="ed-field__input" />
          </Field>
          <Field label="Secondary Guardian Phone">
            <input id="guardian2_phone" type="tel" name="guardian2_phone" value={profile.guardian2_phone || ''} onChange={handleChange} onBlur={handleBlur} className="ed-field__input" />
          </Field>
        </div>
        <Field label="Secondary Guardian Email">
          <input id="guardian2_email" type="email" name="guardian2_email" value={profile.guardian2_email || ''} onChange={handleChange} onBlur={handleBlur} className="ed-field__input" />
        </Field>
      </div>

      {/* Documents — uploaded by the athlete/guardian, visible to them + staff */}
      <SectionCap label="Documents" hint="VISIBLE TO YOU & PROZPECTS STAFF" />
      <DocumentsPanel
        visibility="shared" title="Documents"
        hint="Upload documents (PDF, DOC, images). Visible to you, your guardians, and ProZpects staff."
      />

      {previewModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setPreviewModal(null)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: '90vw', height: '90vh', backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}>
            <Button onClick={() => setPreviewModal(null)} aria-label="Close preview" variant="secondary" iconOnly
              style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>
              &times;
            </Button>
            <iframe
              src={previewModal === 'card' ? `/athlete/${user?.uid || '0'}/${profileUrl}/card` : `/athlete/${user?.uid || '0'}/${profileUrl}`}
              title={previewModal === 'card' ? 'Player Card Preview' : 'Public Profile Preview'}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
